import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { styled } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { getDocument } from '@atlaskit/browser-apis';
import ErrorBoundary from '@atlassian/jira-error-boundary/src/ErrorBoundary.tsx';
import {
	useBottomRightCornerActions,
	useBottomRightCornerContainer,
} from '../../../controllers/layout-controller/consumers/bottom-right-corner-controller/index.tsx';
import type { Props } from './types.tsx';

const OutletWithPortal = ({ orderFromRight, children, id, testId }: Props) => {
	const [portalWrapper, setPortalWrapper] = useState<HTMLDivElement | null>(null);

	const container = useBottomRightCornerContainer();

	const { insertBottomRightCornerPortal, removeBottomRightCornerPortal } =
		useBottomRightCornerActions();

	useEffect(() => {
		// Portal wrapper creation deferred to after hydration is completed
		const document = getDocument();
		if (document != null) {
			const portalEl = document.createElement('div');
			if (testId) {
				portalEl.setAttribute('data-testid', testId);
			}
			setPortalWrapper(portalEl);
		}
	}, [testId]);

	useEffect(() => {
		if (!portalWrapper) {
			return;
		}

		if (container) {
			const indexToRender = insertBottomRightCornerPortal({ orderFromRight, id });
			container.insertBefore(portalWrapper, container.children[indexToRender]);
		}

		return () => {
			container?.removeChild(portalWrapper);
			removeBottomRightCornerPortal(id);
		};
	}, [
		container,
		id,
		orderFromRight,
		insertBottomRightCornerPortal,
		removeBottomRightCornerPortal,
		portalWrapper,
	]);

	if (portalWrapper != null) {
		return createPortal(<StyledOutlet>{children}</StyledOutlet>, portalWrapper);
	}
	return null;
};

export const JiraBottomRightCornerOutlet = (props: Props) => (
	<ErrorBoundary
		id="bottom-right-corner-outlet"
		packageName="jira-bottom-right-corner-controller"
		extraEventData={{ portalId: props.id }}
	>
		<OutletWithPortal {...props} />
	</ErrorBoundary>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledOutlet = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> *': {
		pointerEvents: 'auto',
		marginLeft: token('space.200'),
	},
});
